/** Historique des affiches générées — réutiliser (préremplir le studio) ou re-partager en 1 clic. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Images, Copy, Download, RefreshCw, Share2, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const full = (u) => (u && u.startsWith("http") ? u : `${API_BASE}${u || ""}`);
const KIND = { event: "Événement", dojo: "Dojo", pkh: "PowerKick Her", promo: "Promo", pack: "Pack" };

export default function PosterGallery({ onReuse }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/poster/list?limit=60").then((r) => setItems(r.data.posters || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Légende copiée ✓"); } catch { toast.error("Copie impossible"); } };
  const share = (p) => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(full(p.url))}`, "_blank", "noopener");
  const remove = async (p) => {
    if (!window.confirm("Retirer cette affiche de l'historique ?")) return;
    try { await api.delete(`/poster/list/${p.id}`); setItems((x) => x.filter((i) => i.id !== p.id)); toast.success("Affiche retirée"); } catch { toast.error("Suppression impossible"); }
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 mt-6" data-testid="poster-gallery">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Images className="w-5 h-5 text-[#C9A961]" /> Historique des affiches <span className="text-zinc-500 text-sm font-normal">({items.length})</span></h2>
        <button onClick={load} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid="poster-gallery-refresh"><RefreshCw className="w-3.5 h-3.5" /> Actualiser</button>
      </div>
      {loading ? <div className="text-sm text-zinc-500">Chargement…</div> : items.length === 0 ? (
        <div className="text-sm text-zinc-500" data-testid="poster-gallery-empty">Aucune affiche générée pour l'instant — ta première affiche apparaîtra ici.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {items.map((p) => (
            <div key={p.id} className="group rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden flex flex-col" data-testid={`poster-item-${p.id}`}>
              <a href={full(p.url)} target="_blank" rel="noreferrer" className="block aspect-square bg-black overflow-hidden">
                <img src={full(p.url)} alt={p.headline || "Affiche"} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
              </a>
              <div className="p-2.5 flex-1 flex flex-col">
                <div className="text-xs font-semibold text-zinc-100 truncate" title={p.headline}>{p.headline || "Affiche"}</div>
                <div className="text-[10px] text-zinc-500">{KIND[p.kind] || p.kind || "—"} · {p.format || ""} · {(p.created_at || "").slice(0, 10)}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {onReuse && <button onClick={() => onReuse(p)} data-testid={`poster-reuse-${p.id}`} className="text-[11px] px-2 py-1 rounded bg-[#C9A961] text-black font-semibold hover:bg-white">Réutiliser</button>}
                  <button onClick={() => share(p)} title="Partager sur Facebook" data-testid={`poster-share-${p.id}`} className="text-[11px] px-2 py-1 rounded bg-[#1877F2] text-white hover:opacity-90 flex items-center gap-1"><Share2 className="w-3 h-3" /></button>
                  {p.caption && <button onClick={() => copy(p.caption)} title="Copier la légende" className="text-[11px] px-2 py-1 rounded bg-zinc-800 text-zinc-200 hover:bg-zinc-700"><Copy className="w-3 h-3" /></button>}
                  <a href={full(p.url)} download target="_blank" rel="noreferrer" title="Télécharger" className="text-[11px] px-2 py-1 rounded bg-zinc-800 text-zinc-200 hover:bg-zinc-700"><Download className="w-3 h-3" /></a>
                  <button onClick={() => remove(p)} title="Retirer" data-testid={`poster-delete-${p.id}`} className="text-[11px] px-2 py-1 rounded bg-zinc-800 text-zinc-400 hover:text-rose-400 ml-auto"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
